import cn from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

import ChatbotContainer from '@/components/chatbot/ChatbotContainer/ChatbotContainer';
import { EChatbotVariant } from '@/components/chatbot/ChatbotProvider';
import { EHomeSearchWidgetVariant, HomeSearchWidget } from '@/components/ui/HomeSearchWidget';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useExperimentIsEnabled } from '@/services/experiments';
import { EAITripPlannerVariationKey, OptimizelyFlags } from '@/services/experiments/flags';

// We can't use sticky alone, since we can't render it at the root level.
// Making it fixed only when stuck, is adding some level of unresponsiveness.
// A combination of sticky and fixed is solving the issue, and allowing us
// to have the same behavior and render in the right place.
export const StickySearchWidget = () => {
  const { isAboveTablet } = useBreakpoint();

  const aiTripPlannerIsEnabled = useExperimentIsEnabled(OptimizelyFlags.AI_TRIP_PLANNER, {
    enabledVariationKeys: [EAITripPlannerVariationKey.HOME_PAGE],
  });
  const showChatButton = aiTripPlannerIsEnabled;

  const containerRef = useRef<HTMLDivElement>(null);
  const [isStuck, setIsStuck] = useState(false);

  useEffect(() => {
    if (isAboveTablet || !containerRef.current) return setIsStuck(false);

    const observer = new IntersectionObserver(
      ([entry]) => setIsStuck(!!entry && entry.intersectionRatio < 1),
      { rootMargin: '-1px 0px 0px 0px', threshold: [1] },
    );

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [isAboveTablet]);

  return (
    <>
      <div ref={containerRef} className="sticky top-0 z-20 mb-10 md:relative lg:mb-20">
        <div className="-mx-6 md:mx-0 min-h-[64px] md:min-h-[48px] lg:min-h-[100px] xl:h-[100px]">
          <div
            style={{
              backgroundColor: isStuck
                ? 'var(--page-header-bg-color)'
                : 'var(--page-content-bg-color)',
            }}
            className={cn(
              'px-6 py-2 md:p-0 transition-[background-color] duration-700 ease-in-out',
              {
                'fixed top-0 left-0 right-0 z-1299 shadow-500': isStuck,
              },
            )}>
            <div className="flex flex-col items-center justify-between gap-4 xl:flex-row">
              <HomeSearchWidget variant={EHomeSearchWidgetVariant.RvRentals} />
              {showChatButton && (
                <div className="hidden md:block">
                  <ChatbotContainer variant={EChatbotVariant.HOME} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {showChatButton && (
        <div className="block px-6 pb-6 -mt-6 md:hidden">
          <ChatbotContainer variant={EChatbotVariant.HOME} />
        </div>
      )}
    </>
  );
};
